import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi, deballer } from './client';
import type { components } from './genere';

export type EntrepriseAdminDto = components['schemas']['EntrepriseAdminDto'];
export type UtilisateurAdminDto = components['schemas']['UtilisateurAdminDto'];
export type AbonnementAdminDto = components['schemas']['AbonnementAdminDto'];
export type UsageApiAdminDto = components['schemas']['UsageApiAdminDto'];
export type EntreeAuditDto = components['schemas']['EntreeAuditDto'];
export type TableauDeBordDto = components['schemas']['TableauDeBordDto'];
export type RapportIntegriteAuditDto = components['schemas']['RapportIntegriteAuditDto'];
export type CouponDto = components['schemas']['CouponDto'];
export type DemandeRgpdDto = components['schemas']['DemandeRgpdDto'];

/**
 * Hooks de l'administration.
 *
 * PAS DE PREFIXE D'ENTREPRISE DANS LES CLES DE CACHE, contrairement a l'app utilisateur.
 * C'est cohérent et non un oubli : cet outil n'a pas d'entreprise active, il regarde
 * au-dessus. Le prefixe `admin` isole simplement ce cache de tout le reste.
 *
 * <para>
 * APRES UN ACTE, ON INVALIDE LARGEMENT — y compris le journal d'audit. Un acte vient
 * d'ecrire une entree ; si l'ecran du journal montrait encore l'etat d'avant, l'exploitant
 * pourrait croire que sa trace n'a pas ete prise. La tracabilite n'a de valeur que si elle
 * est visible immediatement.
 * </para>
 */

const CLE = ['admin'] as const;

function useInvalidation() {
  const client = useQueryClient();

  return () => {
    void client.invalidateQueries({ queryKey: CLE });
  };
}

/* ═══════════════════════ Tableau de bord ═══════════════════════ */

export function useTableauDeBord() {
  return useQuery({
    queryKey: [...CLE, 'tableau-de-bord'],
    queryFn: () =>
      deballer(clientApi.GET('/v1/admin/tableau-de-bord', {})) as Promise<TableauDeBordDto>,
  });
}

/* ═══════════════════════ Entreprises ═══════════════════════════ */

export function useEntreprises(parametres: { recherche?: string; suspendues?: boolean } = {}) {
  const { recherche, suspendues } = parametres;

  return useQuery({
    queryKey: [...CLE, 'entreprises', { recherche, suspendues }],
    placeholderData: (precedent) => precedent,
    queryFn: () =>
      deballer(
        clientApi.GET('/v1/admin/entreprises', {
          params: { query: { recherche, suspendues, limite: 100 } },
        }),
      ) as Promise<EntrepriseAdminDto[]>,
  });
}

/**
 * Fiche d'une entreprise.
 *
 * `enabled` pilote par l'appelant : CETTE REQUETE ECRIT AU JOURNAL D'AUDIT cote serveur.
 * La declencher au chargement de la liste tracerait une consultation que personne n'a
 * demandee, et noierait le journal sous des entrees fausses.
 */
export function useEntreprise(entrepriseId: string | null) {
  return useQuery({
    queryKey: [...CLE, 'entreprise', entrepriseId],
    enabled: entrepriseId !== null,
    // Pas de cache : rouvrir une fiche doit reellement retracer une consultation.
    staleTime: 0,
    gcTime: 0,
    queryFn: () =>
      deballer(
        clientApi.GET('/v1/admin/entreprises/{entrepriseId}', {
          params: { path: { entrepriseId: entrepriseId as string } },
        }),
      ) as Promise<EntrepriseAdminDto>,
  });
}

export function useSuspendreEntreprise() {
  const invalider = useInvalidation();

  return useMutation({
    mutationFn: (variables: { entrepriseId: string; motif: string }) =>
      deballer(
        clientApi.POST('/v1/admin/entreprises/{entrepriseId}/suspendre', {
          params: { path: { entrepriseId: variables.entrepriseId } },
          body: { motif: variables.motif },
        }),
      ) as Promise<void>,
    onSuccess: invalider,
  });
}

export function useReactiverEntreprise() {
  const invalider = useInvalidation();

  return useMutation({
    mutationFn: (entrepriseId: string) =>
      deballer(
        clientApi.POST('/v1/admin/entreprises/{entrepriseId}/reactiver', {
          params: { path: { entrepriseId } },
        }),
      ) as Promise<void>,
    onSuccess: invalider,
  });
}

/* ═══════════════════════ Comptes ═══════════════════════════════ */

export function useUtilisateurs(recherche?: string) {
  return useQuery({
    queryKey: [...CLE, 'utilisateurs', { recherche }],
    placeholderData: (precedent) => precedent,
    queryFn: () =>
      deballer(
        clientApi.GET('/v1/admin/utilisateurs', {
          params: { query: { recherche, limite: 100 } },
        }),
      ) as Promise<UtilisateurAdminDto[]>,
  });
}

export function useSuspendreUtilisateur() {
  const invalider = useInvalidation();

  return useMutation({
    mutationFn: (variables: { utilisateurId: string; motif: string }) =>
      deballer(
        clientApi.POST('/v1/admin/utilisateurs/{utilisateurId}/suspendre', {
          params: { path: { utilisateurId: variables.utilisateurId } },
          body: { motif: variables.motif },
        }),
      ) as Promise<void>,
    onSuccess: invalider,
  });
}

export function useReactiverUtilisateur() {
  const invalider = useInvalidation();

  return useMutation({
    mutationFn: (utilisateurId: string) =>
      deballer(
        clientApi.POST('/v1/admin/utilisateurs/{utilisateurId}/reactiver', {
          params: { path: { utilisateurId } },
        }),
      ) as Promise<void>,
    onSuccess: invalider,
  });
}

export function useReinitialiserMotDePasse() {
  const invalider = useInvalidation();

  return useMutation({
    mutationFn: (utilisateurId: string) =>
      deballer(
        clientApi.POST('/v1/admin/utilisateurs/{utilisateurId}/reinitialiser-mot-de-passe', {
          params: { path: { utilisateurId } },
        }),
      ) as Promise<void>,
    onSuccess: invalider,
  });
}

/* ═══════════════════════ Abonnements, coupons, usage ═══════════ */

export function useAbonnements(statut?: string) {
  return useQuery({
    queryKey: [...CLE, 'abonnements', { statut }],
    queryFn: () =>
      deballer(
        clientApi.GET('/v1/admin/abonnements', { params: { query: { statut } } }),
      ) as Promise<AbonnementAdminDto[]>,
  });
}

export function useUsageApi(periode: string) {
  return useQuery({
    queryKey: [...CLE, 'usage-api', periode],
    queryFn: () =>
      deballer(
        clientApi.GET('/v1/admin/usage-api', { params: { query: { periode } } }),
      ) as Promise<UsageApiAdminDto[]>,
  });
}

export function useCoupons() {
  return useQuery({
    queryKey: [...CLE, 'coupons'],
    queryFn: () => deballer(clientApi.GET('/v1/admin/coupons', {})) as Promise<CouponDto[]>,
  });
}

export function useCreerCoupon() {
  const invalider = useInvalidation();

  return useMutation({
    mutationFn: (corps: {
      code: string;
      type: components['schemas']['TypeRemiseCoupon'];
      valeur: number;
      dateMax?: string | null;
      usageMax?: number | null;

      // Nombre de prelevements remises. -1 = illimite (traduit par l'API).
      dureeMois?: number | null;
    }) => deballer(clientApi.POST('/v1/admin/coupons', { body: corps })) as Promise<CouponDto>,
    onSuccess: invalider,
  });
}

export function useDesactiverCoupon() {
  const invalider = useInvalidation();

  return useMutation({
    mutationFn: (couponId: string) =>
      deballer(
        clientApi.DELETE('/v1/admin/coupons/{couponId}', { params: { path: { couponId } } }),
      ) as Promise<void>,
    onSuccess: invalider,
  });
}

/* ═══════════════════════ RGPD ═══════════════════════════════════ */

export function useDemandesRgpd() {
  return useQuery({
    queryKey: [...CLE, 'rgpd'],
    queryFn: () =>
      deballer(clientApi.GET('/v1/admin/rgpd/demandes', {})) as Promise<DemandeRgpdDto[]>,
  });
}

export function useEffacerDemandeRgpd() {
  const invalider = useInvalidation();

  return useMutation({
    mutationFn: (demandeId: string) =>
      deballer(
        clientApi.POST('/v1/admin/rgpd/demandes/{demandeId}/effacer', {
          params: { path: { demandeId } },
        }),
      ),
    onSuccess: invalider,
  });
}

export function useRefuserDemandeRgpd() {
  const invalider = useInvalidation();

  return useMutation({
    mutationFn: (variables: { demandeId: string; motif: string }) =>
      deballer(
        clientApi.POST('/v1/admin/rgpd/demandes/{demandeId}/refuser', {
          params: { path: { demandeId: variables.demandeId } },
          body: { motif: variables.motif },
        }),
      ),
    onSuccess: invalider,
  });
}

export function useExporterEntreprise() {
  const invalider = useInvalidation();

  return useMutation({
    mutationFn: (entrepriseId: string) =>
      deballer(
        clientApi.POST('/v1/admin/rgpd/entreprises/{entrepriseId}/export', {
          params: { path: { entrepriseId } },
        }),
      ),
    onSuccess: invalider,
  });
}

/* ═══════════════════════ Journal d'audit ═══════════════════════ */

export interface FiltresJournal {
  entrepriseId?: string;
  action?: string;
  donneesPersonnellesSeulement?: boolean;
}

export function useJournalAudit(filtres: FiltresJournal = {}) {
  return useQuery({
    queryKey: [...CLE, 'journal', filtres],
    placeholderData: (precedent) => precedent,
    queryFn: () =>
      deballer(
        clientApi.GET('/v1/admin/journal-audit', {
          params: {
            query: {
              entrepriseId: filtres.entrepriseId,
              action: filtres.action,
              donneesPersonnellesSeulement: filtres.donneesPersonnellesSeulement,
              limite: 200,
            },
          },
        }),
      ) as Promise<EntreeAuditDto[]>,
  });
}

export function useIntegriteJournal() {
  return useQuery({
    queryKey: [...CLE, 'journal-integrite'],
    queryFn: () =>
      deballer(
        clientApi.GET('/v1/admin/journal-audit/integrite', {}),
      ) as Promise<RapportIntegriteAuditDto>,
  });
}
